import { useState, useCallback } from "react";
import { JobDescriptionEntity } from "../Entities/JobDescription";
import { useLoader } from "../Context/LoaderContext";
import { openNotification } from "../Components/Notification";
import jobDescriptionService from "../Service/job.description.service";
import interviewService from "../Service/interview.service";
import { InterviewRound } from "../Dto/GenerateJobDescription.dto";
import { parseJobPostDates } from "../Utils/generic-functions";
import useAccess from "./useAccess";
import { message } from "antd";

interface UseJobPostReturn {
  jobPost: JobDescriptionEntity | null;
  isLoading: boolean;
  interviewRoundDetails: InterviewRound[] | undefined;
  fetchJobPost: (id: string) => Promise<void>;
  toggleJobStatus: (checked: boolean) => Promise<boolean | undefined>;
}

export const useJobPost = (jobPostId: string): UseJobPostReturn => {
  const [jobPost, setJobPost] = useState<JobDescriptionEntity | null>(null);
  const [isLoading] = useState(false);
  const [interviewRoundDetails, setInterviewRoundDetails] = useState<
    InterviewRound[] | undefined
  >(undefined);
  const { setLoading } = useLoader();
  const {
    currentLimits,
    currentSubscription,
    changeSubscriptionMeasurements,
    currentPlan,
  } = useAccess();

  const fetchJobPost = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const jobPost = await jobDescriptionService.getJobDescriptionById(id);
        // Parse dates using the utility function
        const parsedJobPost = parseJobPostDates(jobPost);
        setJobPost(parsedJobPost);

        // Fetch interview round details
        const interviewRoundDetails =
          await interviewService.getAllInterviewRoundsSpecificToJob(id);
        if (interviewRoundDetails !== undefined) {
          setInterviewRoundDetails(interviewRoundDetails);
        }
      } catch (error) {
        console.error("Failed to fetch job post:", error);
        message.error("Failed to fetch job post details");
      } finally {
        setLoading(false);
      }
    },
    [setLoading],
  );

  const toggleJobStatus = useCallback(
    async (checked: boolean) => {
      if (!jobPostId || !jobPost) return;

      const status = checked ? "publish" : "unpublish";
      setLoading(true);

      try {
        if (
          status === "publish" &&
          currentSubscription &&
          currentPlan &&
          currentLimits?.jobPosts !== undefined
        ) {
          if (
            currentLimits?.jobPosts !== -1 &&
            currentSubscription.measurements.jobPosts >= currentLimits?.jobPosts
          ) {
            setLoading(false);
            openNotification(
              "error",
              "Job post limit reached",
              "Please upgrade your plan to publish more job posts",
            );

            return;
          }
        }

        const updatedJobPost =
          await jobDescriptionService.updateJobDescriptionStatus(
            jobPostId,
            status,
          );

        if (status === "publish") {
          changeSubscriptionMeasurements(1, "jobPosts");
        } else {
          changeSubscriptionMeasurements(-1, "jobPosts");
        }

        setJobPost(updatedJobPost);
        message.success(`Job status ${status}ed successfully`);        
        return status === "publish" ? true : false;
      } catch (error) {
        console.error("Failed to toggle job status:", error);
        message.error(`Failed to ${status} the job status`);
      } finally {
        setLoading(false);
      }
    },
    [
      jobPostId,
      jobPost,
      currentSubscription,
      currentPlan,
      currentLimits,
      setLoading,
      changeSubscriptionMeasurements,
    ],
  );

  return {
    jobPost,
    interviewRoundDetails,
    fetchJobPost,
    isLoading,
    toggleJobStatus,
  };
};
